import { useCallback, useEffect, useState } from "react";
import { FolderOpen, Copy, Check, Pencil, Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MAX = 10;
const EMPTY = { name: "", path: "" };
const Kbd = ({ children }) => <kbd className="border border-foreground/60 bg-background px-1 py-px font-mono-num text-[10px] leading-none">{children}</kbd>;

async function copyText(text) {
  if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return; }
  const ta = document.createElement("textarea");
  ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
}

// Kaart tussen de URL-snelkoppelingen: klik op een map = pad naar het klembord.
export default function FolderShortcutsCard() {
  const [folders, setFolders] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");

  const load = useCallback(async () => {
    try { setFolders((await api.get("/admin/folders")).data.folders); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (f) => {
    try {
      await copyText(f.path);
      setCopied(f.id);
      setTimeout(() => setCopied(""), 1800);
      toast.success(`Pad van "${f.name}" gekopieerd — Win+E → Ctrl+L → Ctrl+V → Enter`, { duration: 5000 });
    } catch { toast.error("Kopiëren mislukt — selecteer het pad handmatig."); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/folders", { folders: editing.map(({ name, path }) => ({ name: name.trim(), path: path.trim() })) });
      setFolders(data.folders); setEditing(null); toast.success("Mappen opgeslagen.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const update = (i, patch) => setEditing((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setEditing((l) => { const n = [...l]; const j = i + d; if (j < 0 || j >= n.length) return l; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const input = "w-full border-2 border-foreground bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary";
  const startEdit = () => setEditing((folders || []).length ? folders.map((f) => ({ ...f })) : [{ ...EMPTY }]);

  return (
    <>
      <div className="bg-card border-2 border-foreground heavy-shadow p-5 flex flex-col gap-3" data-testid="admin-folders-card">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 border-2 border-foreground bg-accent text-accent-foreground flex items-center justify-center shrink-0">
              <FolderOpen className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <div className="stat-label truncate">Mappen op je pc</div>
              <div className="font-display font-black text-2xl uppercase tracking-tight truncate">Mappen</div>
              <div className="text-xs text-muted-foreground mt-0.5">Klik op een map = pad gekopieerd</div>
            </div>
          </div>
          <button type="button" onClick={startEdit} aria-label="Mappen bewerken" title="Mappen bewerken" data-testid="admin-folders-edit"
            className="p-2 border-2 border-foreground hover:bg-foreground hover:text-background transition-colors shrink-0"><Pencil className="w-4 h-4" /></button>
        </div>

        {folders === null ? <div className="text-xs text-muted-foreground">Laden…</div> : folders.length === 0 ? (
          <button type="button" onClick={startEdit} data-testid="admin-folders-empty"
            className="border-2 border-dashed border-foreground/40 p-3 text-left text-xs text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
            Nog geen mappen. Klik om bijvoorbeeld <span className="font-mono-num">C:\Users\…\Downloads</span> of je mailteksten-map toe te voegen.
          </button>
        ) : (
          <ul className="divide-y-2 divide-foreground border-2 border-foreground" data-testid="admin-folders-list">
            {folders.map((f, i) => {
              const done = copied === f.id;
              return (
                <li key={f.id}>
                  <button type="button" onClick={() => copy(f)} title={`${f.path}\nKlik om te kopiëren`} data-testid={`admin-folder-copy-${i}`}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${done ? "bg-positive/30" : "hover:bg-foreground hover:text-background group"}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate">{f.name}</div>
                      <div className={`font-mono-num text-[11px] truncate ${done ? "text-foreground" : "text-muted-foreground group-hover:text-background/80"}`}>{f.path}</div>
                    </div>
                    {done ? <Check className="w-4 h-4 shrink-0 text-foreground" /> : <Copy className="w-4 h-4 shrink-0 opacity-60 group-hover:opacity-100" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Daarna: <Kbd>Win</Kbd>+<Kbd>E</Kbd> → <Kbd>Ctrl</Kbd>+<Kbd>L</Kbd> → <Kbd>Ctrl</Kbd>+<Kbd>V</Kbd> → <Kbd>Enter</Kbd> en je zit in de map.
        </p>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !saving) setEditing(null); }}>
        <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow max-h-[85vh] overflow-y-auto" data-testid="admin-folders-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight">Mappen</DialogTitle>
            <DialogDescription>Naam en pad van mappen op je pc (bijv. <span className="font-mono-num">C:\Users\Jan\Downloads</span> of <span className="font-mono-num">\\server\gedeeld</span>). Opgeslagen bij de beheerinstellingen, dus beschikbaar in elke browser. Maximaal {MAX}.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {editing.map((f, i) => (
                <div key={i} className="border-2 border-foreground p-3 grid gap-2 sm:grid-cols-[1fr_2fr_auto]" data-testid={`admin-folder-row-${i}`}>
                  <label className="block"><span className="stat-label">Naam</span>
                    <input value={f.name} maxLength={40} onChange={(e) => update(i, { name: e.target.value })} data-testid={`admin-folder-name-${i}`} className={input} placeholder="bijv. Downloads" /></label>
                  <label className="block"><span className="stat-label">Pad</span>
                    <input value={f.path} maxLength={300} onChange={(e) => update(i, { path: e.target.value })} data-testid={`admin-folder-path-${i}`} className={`${input} font-mono-num`} placeholder="C:\Users\…\Downloads" spellCheck={false} /></label>
                  <div className="flex items-end gap-1 justify-end">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 border-2 border-foreground disabled:opacity-30" aria-label="Omhoog"><ArrowUp className="w-4 h-4" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === editing.length - 1} className="p-1.5 border-2 border-foreground disabled:opacity-30" aria-label="Omlaag"><ArrowDown className="w-4 h-4" /></button>
                    <button type="button" onClick={() => setEditing((x) => x.filter((_, j) => j !== i))} className="p-1.5 border-2 border-destructive text-destructive" aria-label="Verwijderen" data-testid={`admin-folder-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {editing.length === 0 && <div className="text-sm text-muted-foreground">Geen mappen — opslaan maakt de kaart leeg.</div>}
              <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                <button type="button" onClick={() => setEditing((x) => [...x, { ...EMPTY }])} disabled={editing.length >= MAX} data-testid="admin-folder-add"
                  className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40"><Plus className="w-3.5 h-3.5" /> Map toevoegen</button>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditing(null)} disabled={saving} className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Annuleren</button>
                  <button type="button" onClick={save} disabled={saving || editing.some((f) => !f.name.trim() || !f.path.trim())} data-testid="admin-folders-save"
                    className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Opslaan
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
